import {
    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR,
    WIDGET_VERSION,
} from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { globalProductIdManagerToken } from "@/tokens";
import {
    AverageReviewsWidgetDefinition,
    ReviewsStarRatingWidgetDefinition,
} from "@apps/reviews/definitions";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { ProductIdFromStaticProvider } from "@lib/ProductIdManager/ProductIdFromStaticProvider";
import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import IntersectionObserverInjector from "@ui-utils/Injector/IntersectionObserverInjector";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import {
    parseIntDataAttr,
    parseIntFromGidDataAttr,
    parseOptionalBooleanDataAttr,
    parseStringDataAttr,
} from "@ui-utils/Injector/utils";
import { container } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

const REVIEW_PRODUCT_ID = "gw-product-id";
const ELEMENT_TO_SCROLL_ID = "gw-element-to-scroll-id";
const ELEMENT_TO_CLICK_ID = "gw-element-to-click-id";

class ReviewsStarRatingWidgetInjector {
    private ioi: IntersectionObserverInjector;

    constructor(
        private readonly placeholderSelector: string,
        private readonly logger: LoggerInterface,
        private readonly eventsCollector: AnalyticsEventsCollectorInterface,
        private readonly analyticsEventsFactory: AnalyticsEventsFactory,
        private readonly dynamicImportManager: DynamicImportManager,
        private readonly moi: MutationObserverInjector
    ) {
        this.ioi = new IntersectionObserverInjector(
            IntersectionObserver,
            this.moi,
            this.placeholderSelector,
            {
                rootMargin: "0px 0px 50% 0px",
            }
        );
    }

    start(): void {
        this.logger.debug("ReviewsStarRatingWidgetInjector start", {
            placeholderSelector: this.placeholderSelector,
        });

        this.ioi.targets$.subscribe((placeholder) => {
            this.logger?.debug(
                "ReviewsStarRatingWidgetInjector found placeholder",
                {
                    placeholder,
                }
            );

            const productIdFromDataAttr =
                parseIntDataAttr(placeholder, REVIEW_PRODUCT_ID) ||
                parseIntFromGidDataAttr(placeholder, REVIEW_PRODUCT_ID);

            const elementToScrollId = parseStringDataAttr(
                placeholder,
                ELEMENT_TO_SCROLL_ID
            );
            const elementToClickId = parseStringDataAttr(
                placeholder,
                ELEMENT_TO_CLICK_ID
            );

            if (productIdFromDataAttr === null) {
                this.logger.error(
                    `ReviewsStarRatingWidget placeholder does not have data-${REVIEW_PRODUCT_ID} attribute.`
                );
            }

            const productIdManager =
                productIdFromDataAttr === null
                    ? container.resolve(globalProductIdManagerToken)
                    : new ProductIdManager();

            if (productIdFromDataAttr !== null) {
                productIdManager.registerProvider(
                    new ProductIdFromStaticProvider(productIdFromDataAttr)
                );
            }

            void (async () => {
                const customizationManager =
                    container.resolve(CustomizationManager);

                const widgetVersion = customizationManager.defineWidgetVersion({
                    widgetName: REVIEWS_WIDGET_NAMES.REVIEWS_STAR_RATING_WIDGET,
                    placeholder,
                });

                if (widgetVersion === WIDGET_VERSION.V1) {
                    const AverageReviewsWidget =
                        await this.dynamicImportManager.importDefault(
                            AverageReviewsWidgetDefinition,
                            true
                        );

                    const widget = new AverageReviewsWidget(
                        {
                            target: placeholder,
                            isWidgetShadowRootEnabled:
                                parseOptionalBooleanDataAttr(
                                    placeholder,
                                    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                                ),
                            elementToScrollId: elementToScrollId ?? "",
                            elementToClickId: elementToClickId ?? "",
                        },
                        this.logger,
                        productIdManager
                    );

                    await widget.init();
                    this.eventsCollector.pushEvent(
                        this.analyticsEventsFactory.createWidgetInjected(
                            REVIEWS_WIDGET_NAMES.AVERAGE_REVIEWS_WIDGET
                        )
                    );

                    dispatchSdkEvent(
                        new WidgetInjectedSdkEvent(
                            REVIEWS_WIDGET_NAMES.AVERAGE_REVIEWS_WIDGET
                        )
                    );
                } else {
                    const ReviewsStarRatingWidget =
                        await this.dynamicImportManager.importDefault(
                            ReviewsStarRatingWidgetDefinition,
                            true
                        );

                    const widget = new ReviewsStarRatingWidget(
                        {
                            target: placeholder,
                            isWidgetShadowRootEnabled:
                                parseOptionalBooleanDataAttr(
                                    placeholder,
                                    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                                ),
                            elementToScrollId: elementToScrollId ?? "",
                            elementToClickId: elementToClickId ?? "",
                        },
                        this.logger,
                        productIdManager
                    );

                    await widget.init();

                    this.eventsCollector.pushEvent(
                        this.analyticsEventsFactory.createWidgetInjected(
                            REVIEWS_WIDGET_NAMES.REVIEWS_STAR_RATING_WIDGET
                        )
                    );

                    dispatchSdkEvent(
                        new WidgetInjectedSdkEvent(
                            REVIEWS_WIDGET_NAMES.REVIEWS_STAR_RATING_WIDGET
                        )
                    );
                }
            })();
        });

        this.ioi.start();
    }
}

export default ReviewsStarRatingWidgetInjector;
